import { TranslationKey } from '@Localization';
import { useConfiguration } from 'common/hooks/useConfiguration';
import { useInjection } from 'inversify-react';
import * as path from 'path';
import { browserHistory } from 'usehistory';
import { ISettingsStore } from '../../../modules/settings/SettingsContext';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ACDLError, ForbiddenError, InputStatus } from '../../models/error.model';
import { Home } from '../../routes/config/Home';
import { IMainApiService, IPagedResult } from '../../services/api.service';
import { IApiServiceImageUpload } from '../../services/api.service.imageUpload';
import { CacheManager, ICacheManager } from '../../services/cacheManager';
import { LOW_QUALITY_PRELOAD_MIN_IMAGE_SIZE } from '../annotation/annotations.interface';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { ImagePreviewCache } from './cache/imagePreview.cache';
import {
  DefaultPaging,
  IDatasetDetails,
  IDatasetDetailsImage,
  IDatasetDetailsProjects,
  IDatasetPreviewImage as IDatasetPreviewImageInfo,
  IDatasetsDetailsStore,
  IImageUsageInfo,
  IPreviewPaging,
  IPreviewPagingItem,
} from './datasetsDetails.store';

export const ReservedFileNames = [
  'CON',
  'PRN',
  'AUX',
  'NUL',
  'COM1',
  'COM2',
  'COM3',
  'COM4',
  'COM5',
  'COM6',
  'COM7',
  'COM8',
  'COM9',
  'COM0',
  'LPT1',
  'LPT2',
  'LPT3',
  'LPT4',
  'LPT5',
  'LPT6',
  'LPT7',
  'LPT8',
  'LPT9',
  'LPT0',
  'CLOCK$',
];

export interface IDatasetDetailsService {
  bulkDeleteImages(datasetId: string, imagesToBulkDelete: string[]): Promise<void>;
  getDatasetDetailsAsync(datasetId: string): Promise<void>;
  getDatasetUserSettingsAsync(datasetId: string): Promise<void>;
  getDatasetImagesAsync(datasetId: string, pageNumber: number, pageSize: number, orderBy: string | undefined, orderType: string | undefined): Promise<void>;
  refreshImages(datasetId: string, pageNumber: number, pageSize: number, orderBy: string | undefined, orderType: string | undefined): Promise<void>;
  downloadDatasetAsync(datasetId: string): Promise<void | ACDLError>;
  downloadImageAsync(imageId: string): Promise<void | ACDLError>;
  deleteImageAsync(imageId: string): Promise<void | ACDLError>;
  showImageAsync(datasetId: string, imageId: string, size: number): Promise<void>;
  getDatasetImageInfoAsync(datasetId: string, imageId: string): Promise<IDatasetPreviewImageInfo | ACDLError>;
  getDatasetProjectsAsync(datasetId: string): Promise<void>;
  updateDatasetDetailsAsync(detailsPayload: IUpdateDatasetDetailsPayload): Promise<void>;
  validateDatasetNameAsync(name: string, datasetId: string): Promise<TranslationKey[]>;
  getPreviewPaging(datasetId: string, lastUpdate?: string): Promise<IPreviewPaging | ACDLError>;
  downloadSelectedImagesAsync(datasetId: string, imagesIds: string[]): Promise<void | ACDLError>;
  getImageUsageInfo(datasetId: string, imageId: string): Promise<IImageUsageInfo | ACDLError>;
  publishDatasetAsync(datasetId: string): Promise<void | ACDLError>;
  deleteDatasetAsync(datasetId: string): Promise<void | ACDLError>;
  setDefaultImagesPaging(): void;
  changeDatasetProjectsOrder(datasetId: string, orderBy: string | undefined, orderType: string | undefined): Promise<void>;
  changeDatasetProjectsPagination(datasetId: string, pageNumber: number, pageSize: number): Promise<void>;
  getDatasetDetailsImageViewAsync(datasetId: string): Promise<boolean>;
  renameImageAsync(imageId: string, name: string, datasetId: string): Promise<void | ACDLError>;
  store: IDatasetsDetailsStore;
  checkImageNameUniquenessAsync(imageId: string, datasetId: string, name: string): Promise<boolean | ACDLError>;
  validateImageNameAsync(imageName: string, imageId: string, datasetId: string): Promise<InputStatus>;
  isValidFileName(imageName: string): boolean;
}

interface IUpdateDatasetDetailsPayload {
  datasetId: string;
  name: string;
  description: string;
  author: string;
  termsOfUse: string;
}

export const useDatasetDetailsService = (): IDatasetDetailsService => {
  const apiService = useInjection(IMainApiService.$);
  const uploadApiService = useInjection(IApiServiceImageUpload.$);
  const notificationsService = useInjection(INotificationsService.$);
  const store = useInjection(IDatasetsDetailsStore.$);
  const config = useConfiguration();
  const cacheManager = useInjection(ICacheManager.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);

  //store.activePreloads.replace([]);

  const getDatasetDetailsImageViewAsync = async (datasetId: string): Promise<boolean> => {
    const url = `/datasets/userSettings?DatasetId=${datasetId}`;
    const result = await apiService.getAsync<{ orderBy: string; orderType: string }>(url);

    if (result instanceof ACDLError) {
      return false;
    }

    //currently there is no concept of reading react context in injected services
    const settings = JSON.parse(localStorage.getItem('settings') ?? '{}') as ISettingsStore;

    const paging = {
      ...store.detailsImagesPaging,
      pageSize: settings.datasetDetailsImagesPageSize ?? DefaultPaging.pageSize,
      orderBy: result.orderBy ?? DefaultPaging.orderBy,
      orderType: result.orderType ?? DefaultPaging.orderType,
    };

    store.setImagesPaging(paging);

    await getDatasetImagesAsync(
      datasetId,
      store.detailsImagesPaging.pageNumber,
      store.detailsImagesPaging.pageSize,
      store.detailsImagesPaging.orderBy,
      store.detailsImagesPaging.orderType
    );

    return true;
  };

  const publishDatasetAsync = async (datasetId: string): Promise<void | ACDLError> => {
    const url = '/datasets/publish';
    const result = await apiService.postAsync(url, undefined, { params: { datasetId } });
    if (result instanceof ACDLError) {
      return result;
    }
    await getDatasetDetailsAsync(datasetId);
    return Promise.resolve(undefined);
  };

  const deleteDatasetAsync = async (datasetId: string): Promise<void | ACDLError> => {
    const url = '/datasets/remove';
    const result = await apiService.postAsync(url, undefined, { params: { datasetId } });
    if (result instanceof ACDLError) {
      return result;
    }
    return Promise.resolve(undefined);
  };

  const downloadSelectedImagesAsync = async (datasetId: string, imagesIds: string[]): Promise<void | ACDLError> => {
    const result = await uploadApiService.postAsync<string>('/datasets/generatePartialDatasetDownloadId', {
      datasetId,
      imagesIds,
    });

    if (!(result instanceof ACDLError)) {
      const downloadUrl = new URL(result, config.appConfig.imageUploadBaseUrl).toString();
      window.open(downloadUrl, '_self');
    }
  };

  const downloadDatasetAsync = async (datasetId: string): Promise<void | ACDLError> => {
    const result = await uploadApiService.postAsync<string>('/datasets/generateDatasetDownloadId', {
      datasetId,
    });

    if (!(result instanceof ACDLError)) {
      const downloadUrl = new URL(result, config.appConfig.imageUploadBaseUrl).toString();
      window.open(downloadUrl, '_self');
    }
  };

  const bulkDeleteImages = async (datasetId: string, imagesToBulkDelete: string[]): Promise<void> => {
    const result = await apiService.postAsync<{ datasetId: string; imagesToBulkDelete: string[] }>('/Images/BulkDeleteImage', {
      datasetId,
      imagesToBulkDelete,
    });

    store.selectedImages.clear();

    if (result instanceof ACDLError) {
      if (!(result instanceof ForbiddenError)) {
        await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_dataset_details_failed'));
      }
      return;
    }
    const paging = store.detailsImagesPaging;

    await getDatasetImagesAsync(datasetId, paging.pageNumber, paging.pageSize, paging.orderBy, paging.orderType);
    await getDatasetDetailsAsync(datasetId);
  };

  const getDatasetDetailsAsync = async (datasetId: string): Promise<void> => {
    const url = `/datasets/details?DatasetId=${datasetId}`;
    const result = await apiService.getAsync<IDatasetDetails>(url);

    if (result instanceof ACDLError) {
      if (!(result instanceof ForbiddenError)) {
        await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_dataset_details_failed'));
        browserHistory.push(Home.Datasets.List.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
      }
      return;
    }
    if (result.workspaceId !== currentWorkspaceStore.currentWorkspace.id) {
      browserHistory.replace(Home.Datasets.List.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
      return;
    }

    store.setDatasetDetails(result);
  };

  const getDatasetUserSettingsAsync = async (datasetId: string): Promise<void> => {
    const url = `/datasets/userSettings?DatasetId=${datasetId}`;
    const result = await apiService.getAsync<{ orderBy: string; orderType: string }>(url);

    if (result instanceof ACDLError) {
      return;
    }

    const paging = {
      ...DefaultPaging,
      orderBy: result.orderBy ? result.orderBy : DefaultPaging.orderBy,
      orderType: result.orderType ? result.orderType : DefaultPaging.orderType,
    };

    store.setImagesPaging(paging);
  };

  const getDatasetImagesAsync = async (
    datasetId: string,
    pageNumber: number,
    pageSize: number,
    orderBy: string = '',
    orderType: string = ''
  ): Promise<void> => {
    let order = orderBy;
    let type = orderType;

    let url = `/datasets/images?DatasetId=${datasetId}&pageNumber=${pageNumber}&pageSize=${pageSize}&orderBy=${order}&orderType=${type}`;
    let result = await apiService.getAsync<IPagedResult<IDatasetDetailsImage>>(url);

    if (result instanceof ACDLError) {
      order = DefaultPaging.orderBy;
      type = DefaultPaging.orderType;

      url = `/datasets/images?DatasetId=${datasetId}&pageNumber=${pageNumber}&pageSize=${pageSize}&orderBy=${order}&orderType=${type}`;
      result = await apiService.getAsync<IPagedResult<IDatasetDetailsImage>>(url);
      if (result instanceof ACDLError) {
        await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_dataset_images_failed'));
        browserHistory.push(Home.Datasets.List.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
        return;
      }
    }

    if (result.pagesCount < result.pageNumber) {
      await getDatasetImagesAsync(datasetId, result.pagesCount, pageSize, order, type);
    } else {
      store.setImages(
        result.data,
        {
          pageSize,
          orderBy: order,
          orderType: type,
          pageNumber: result.pageNumber,
          pagesCount: result.pagesCount,
          totalCount: result.totalCount,
        },
        result.totalCount
      );
    }
  };

  const refreshImages = async (datasetId: string, pageNumber: number, pageSize: number, orderBy: string, orderType: string): Promise<void> => {
    const url = `/datasets/images?DatasetId=${datasetId}&pageNumber=${pageNumber}&pageSize=${pageSize}&orderBy=${orderBy}&orderType=${orderType}`;
    const result = await apiService.getAsync<IPagedResult<IDatasetDetailsImage>>(url);

    if (result instanceof ACDLError) {
      await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_dataset_images_failed'));

      browserHistory.push(Home.Datasets.List.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
      return;
    }

    if (result.pageNumber !== store.detailsImagesPaging.pageNumber) {
      return;
    }

    if (result.pagesCount < result.pageNumber) {
      await getDatasetImagesAsync(datasetId, result.pagesCount, pageSize, orderBy, orderType);
    } else {
      store.setImages(
        result.data,
        {
          pageSize,
          orderBy,
          orderType,
          pageNumber: result.pageNumber,
          pagesCount: result.pagesCount,
          totalCount: result.totalCount,
        },
        result.totalCount
      );
    }
  };

  const downloadImageAsync = async (imageId: string): Promise<void | ACDLError> => {
    return await apiService.getFileAsync(`/images/DownloadImage/${imageId}`);
  };

  const getPreviewPaging = async (datasetId: string, lastUpdate?: string): Promise<IPreviewPaging | ACDLError> => {
    return await apiService.getAsync<IPreviewPaging>(`/Datasets/PreviewPaging?datasetId=${datasetId}${lastUpdate ? `&lastUpdate=${lastUpdate}` : ''}`);
  };

  const showImageAsync = async (datasetId: string, imageId: string, size: number): Promise<void> => {
    await Promise.all([loadLowQualityImage(datasetId, imageId, size), loadImage(datasetId, imageId)]);
  };

  const getDatasetImageInfoAsync = async (datasetId: string, imageId: string): Promise<ACDLError | IDatasetPreviewImageInfo> => {
    const previewImageInfo = await fetchImageInfoAsync(datasetId, imageId);

    if (previewImageInfo instanceof ACDLError) {
      return previewImageInfo;
    }

    store.imagePreviewInfo = previewImageInfo;

    return previewImageInfo;
  };

  const getImageUsageInfo = async (datasetId: string, imageId: string): Promise<ACDLError | IImageUsageInfo> => {
    const url = `/datasets/${datasetId}/imageUsageInfo/${imageId}`;
    return await apiService.getAsync<IImageUsageInfo>(url);
  };

  const fetchImageInfoAsync = async (datasetId: string, imageId: string): Promise<ACDLError | IDatasetPreviewImageInfo> => {
    const url = `/datasets/${datasetId}/imageInfo/${imageId}`;

    const cache = new ImagePreviewCache(currentWorkspaceStore.currentWorkspace.id, datasetId, imageId);
    const result = await cache.getImageInfoAsync();

    if (result) {
      return result;
    }

    const previewImage = await apiService.getAsync<IDatasetPreviewImageInfo>(url);
    if (previewImage instanceof ACDLError) {
      return previewImage;
    }

    await cache.setImageInfoAsync(previewImage);
    return previewImage;
  };

  const loadLowQualityImage = async (datasetId: string, imageId: string, size: number): Promise<void> => {
    if (store.details.workspaceId !== currentWorkspaceStore.currentWorkspace.id) {
      browserHistory.replace(Home.Datasets.List.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
      return;
    }
    if (size >= LOW_QUALITY_PRELOAD_MIN_IMAGE_SIZE) {
      const imageFile = await apiService.getImageAsync(apiService.getUrl(`/Images/GetImageThumbnail/${imageId}`));
      if (store.imagePreviewInfo.id === imageId && !(imageFile instanceof ACDLError)) {
        store.lowQualityImagePreviewUrl = URL.createObjectURL(imageFile.blob);
      }
    }
    const currentItem = store.previewPaging.items.find((x) => x.imageId === imageId);
    if (currentItem) {
      await preLoadNextLowQualityImages(datasetId, store.previewPaging, currentItem.index);
    }
  };

  const loadImage = async (datasetId: string, imageId: string): Promise<void> => {
    let imageFile = null;

    if (store.details.workspaceId !== currentWorkspaceStore.currentWorkspace.id) {
      browserHistory.replace(Home.Datasets.List.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
      return;
    }

    if (!store.activePreloads.some((x) => x === imageId)) {
      imageFile = await apiService.getImageAsync(apiService.getUrl(`/datasets/${datasetId}/image/${imageId}`));
    }

    if (store.imagePreviewInfo.id === imageId && imageFile !== null && !(imageFile instanceof ACDLError)) {
      store.imagePreviewUrl = URL.createObjectURL(imageFile.blob);
      store.imageAttributes = imageFile.attributes;
    }
    const currentItem = store.previewPaging.items.find((x) => x.imageId === imageId);
    if (currentItem) {
      await preLoadNextImages(datasetId, store.previewPaging, currentItem.index);
    }
  };

  const preLoadNextImages = async (datasetId: string, paging: IPreviewPaging, currentIndex: number): Promise<void> => {
    const cacheAvailable = await CacheManager.checkCacheAvailabilityAsync();
    if (!cacheAvailable) {
      return;
    }

    const range = config.appConfig.imagePreloadRange;

    const imagesToPreLoadIds = paging.items
      .filter((x) => x.index > currentIndex - range && x.index < currentIndex + range && x.index !== currentIndex)
      .sort((n1, n2) => Math.abs(n1.index - currentIndex) - Math.abs(n2.index - currentIndex));

    const preload = async (item: IPreviewPagingItem): Promise<void> => {
      const url = apiService.getUrl(`/datasets/${datasetId}/image/${item.imageId}`);

      if (store.activePreloads.some((x) => x === item.imageId)) {
        return;
      }
      if (await cacheManager.checkIfInCache(url)) {
        return;
      }

      store.activePreloads.push(item.imageId);
      const imageFile = await apiService.getImageAsync(url);
      if (store.imagePreviewInfo.id === item.imageId && !(imageFile instanceof ACDLError)) {
        store.imagePreviewUrl = URL.createObjectURL(imageFile.blob);
        store.imageAttributes = imageFile.attributes;
      }

      const index = store.activePreloads.findIndex((x) => x === item.imageId);
      if (index > -1) {
        store.activePreloads.splice(index, 1);
      }
    };

    await Promise.allSettled(imagesToPreLoadIds.map(preload));
  };

  const preLoadNextLowQualityImages = async (datasetId: string, paging: IPreviewPaging, currentIndex: number): Promise<void> => {
    const cacheAvailable = await CacheManager.checkCacheAvailabilityAsync();
    if (!cacheAvailable) {
      return;
    }

    const range = config.appConfig.imagePreloadRange;
    const imagesToPreLoadIds = paging.items
      .filter((x) => x.index > currentIndex - range && x.index < currentIndex + range && x.index !== currentIndex)
      .sort((n1, n2) => Math.abs(n1.index - currentIndex) - Math.abs(n2.index - currentIndex));

    const preload = async (item: IPreviewPagingItem): Promise<void> => {
      if (item.size >= LOW_QUALITY_PRELOAD_MIN_IMAGE_SIZE) {
        const [blob] = await Promise.all([
          apiService.getImageAsync(apiService.getUrl(`/Images/GetImageThumbnail/${item.imageId}`)),
          fetchImageInfoAsync(datasetId, item.imageId),
        ]);

        if (store.imagePreviewInfo.id === item.imageId && !(blob instanceof ACDLError)) {
          store.lowQualityImagePreviewUrl = URL.createObjectURL(blob.blob);
        }
      } else {
        await fetchImageInfoAsync(datasetId, item.imageId);
      }
    };

    await Promise.allSettled(imagesToPreLoadIds.map(preload));
  };

  const deleteImageAsync = async (imageId: string): Promise<void> => {
    const result = await apiService.postAsync<{ imageId: string }>('/Images/DeleteImage', { imageId });

    if (result instanceof ACDLError) {
      await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'update_dataset_details_failed'));
      return;
    }

    store.details.imagesCount -= 1;
    const removedImage = store.detailsImages.find((img) => img.id === imageId);
    if (removedImage) {
      store.details.imagesSize -= removedImage.size;
      store.detailsImages.remove(removedImage);
      const selectedImage = store.selectedImages.find((si) => si.id === imageId);
      if (selectedImage) {
        store.selectedImages.remove(selectedImage);
      }
    }
  };

  const renameImageAsync = async (imageId: string, name: string, datasetId: string): Promise<undefined | ACDLError> => {
    const result = await apiService.postAsync<{ imageId: string; name: string; datasetId: string }>('/Images/RenameImage', { imageId, name, datasetId });

    if (result instanceof ACDLError) {
      await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'update_dataset_details_failed'));
      return result;
    }

    // when renaming on dataset list update current images list
    const image = store.detailsImages.find((img) => img.id === imageId);
    if (image) {
      image.name = name;
    }
    const cache = new ImagePreviewCache(currentWorkspaceStore.currentWorkspace.id, datasetId, imageId);
    await cache.setNewImageNameAsync(name);
    return undefined;
  };

  const getDatasetProjectsAsync = async (datasetId: string): Promise<void> => {
    const url = '/datasets/projects';
    const result = await apiService.getAsync<IPagedResult<IDatasetDetailsProjects>>(url, {
      params: {
        datasetId,
        ...store.detailsProjectsPaging,
      },
    });

    if (result instanceof ACDLError) {
      if (!(result instanceof ForbiddenError)) {
        await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_dataset_projects_failed'));
      }
      browserHistory.push(Home.Datasets.List.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
      return;
    }

    store.setDatasetDetailsProjects(result.data, {
      ...store.detailsProjectsPaging,
      pageNumber: result.pageNumber,
      pagesCount: result.pagesCount,
      totalCount: result.totalCount,
    });
  };

  const changeDatasetProjectsOrder = async (datasetId: string, orderBy: string = '', orderType: string = ''): Promise<void> => {
    store.setDatasetDetailsProjectsPaging({
      ...store.detailsProjectsPaging,
      orderBy,
      orderType,
    });

    await getDatasetProjectsAsync(datasetId);
  };

  const changeDatasetProjectsPagination = async (datasetId: string, pageNumber: number, pageSize: number): Promise<void> => {
    store.setDatasetDetailsProjectsPaging({
      ...store.detailsProjectsPaging,
      pageNumber,
      pageSize,
    });

    await getDatasetProjectsAsync(datasetId);
  };

  const updateDatasetDetailsAsync = async (detailsPayload: IUpdateDatasetDetailsPayload): Promise<void> => {
    const result = await apiService.postAsync<IUpdateDatasetDetailsPayload>('/Datasets/UpdateDetails', detailsPayload);

    if (result instanceof ACDLError && !(result instanceof ForbiddenError)) {
      await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'update_dataset_details_failed'));
      return;
    }

    store.details.name = detailsPayload.name;
    store.details.description = detailsPayload.description;
    store.details.author = detailsPayload.author;
    store.details.termsOfUse = detailsPayload.termsOfUse;
  };

  const validateDatasetNameAsync = async (name: string, datasetId: string): Promise<TranslationKey[]> => {
    const nameErrorCodes: TranslationKey[] = [];
    const workspaceId = currentWorkspaceStore.currentWorkspace.id;

    if (name === '' && name.length === 0) {
      nameErrorCodes.push('field_cant_be_empty');
    } else {
      const url = '/datasets/CheckDatasetNameUniqueness';
      const params = {
        datasetId,
        workspaceId,
        name,
      };
      const result = await apiService.getAsync<boolean | ACDLError>(url, { params });
      if (result instanceof ACDLError) {
        nameErrorCodes.push(...(result.apiErrorResponse?.errorCodes || ['something_went_wrong']));
      } else if (!result) {
        nameErrorCodes.push('duplicate_dataset_name');
      }
    }
    return nameErrorCodes;
  };

  const setDefaultImagesPaging = (): void => {
    store.setImagesPaging(DefaultPaging);
  };

  const checkImageNameUniquenessAsync = async (imageId: string, datasetId: string, name: string): Promise<boolean | ACDLError> => {
    return apiService.getAsync<boolean>('/Images/CheckImageNameUniqueness', { params: { imageId, datasetId, name } });
  };

  const validateImageNameAsync = async (imageName: string, imageId: string, datasetId: string): Promise<InputStatus> => {
    const errorCodes: TranslationKey[] = [];

    if (imageName.trim() === '') {
      errorCodes.push('field_cant_be_empty');
    }

    const format = /[*:"\\|<>\/?]+/;
    if (format.test(imageName)) {
      errorCodes.push('no_special_characters');
    }

    const baseName = path.basename(imageName, path.extname(imageName));
    if (ReservedFileNames.some((fn) => baseName.toUpperCase() === fn)) {
      errorCodes.push('forbidden_image_name');
    }
    if (/^\.+$/.test(imageName)) {
      errorCodes.push('forbidden_image_name');
    }

    const isUnique = await checkImageNameUniquenessAsync(imageId, datasetId, imageName);
    if (!isUnique) {
      errorCodes.push('duplicate_image_name');
    }

    const nameStatus = InputStatus.buildFrom(errorCodes);
    return nameStatus;
  };

  const isValidFileName = (imageName: string): boolean => {
    if (imageName.length > 255) {
      return false;
    }

    const format = /[*:"\\|<>\/?]+/;
    if (format.test(imageName)) {
      return false;
    }

    const baseName = path.basename(imageName, path.extname(imageName));
    if (ReservedFileNames.some((fn) => baseName.toUpperCase() === fn)) {
      return false;
    }
    if (/^\.+$/.test(imageName)) {
      return false;
    }

    return true;
  };

  return {
    bulkDeleteImages,
    getDatasetDetailsAsync,
    changeDatasetProjectsPagination,
    updateDatasetDetailsAsync,
    validateDatasetNameAsync,
    setDefaultImagesPaging,
    checkImageNameUniquenessAsync,
    validateImageNameAsync,
    isValidFileName,
    getDatasetUserSettingsAsync,
    getDatasetImagesAsync,
    refreshImages,
    downloadDatasetAsync,
    downloadImageAsync,
    deleteImageAsync,
    showImageAsync,
    getDatasetImageInfoAsync,
    getDatasetProjectsAsync,
    changeDatasetProjectsOrder,
    getDatasetDetailsImageViewAsync,
    publishDatasetAsync,
    deleteDatasetAsync,
    renameImageAsync,
    getImageUsageInfo,
    getPreviewPaging,
    downloadSelectedImagesAsync,
    store,
  };
};
